import { useState } from "react";
import api from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, FileStack, FileText, Scale, Lock, Loader2, Building2, UserCheck, Wrench, Sparkles } from "lucide-react";

const VALUE_PROPS = [
  { icon: FileStack, text: "Professional evidence bundle PDF with a full photo appendix" },
  { icon: FileText, text: "AI-assisted deposit return-request & dispute letter drafts you review and edit" },
  { icon: Scale, text: "Deduction dispute matching against your evidence" },
  { icon: UserCheck, text: "Witness links — a friend can record they saw your documentation" },
  { icon: Wrench, text: "Maintenance share links to send a repair issue to your landlord" },
];

export function UpgradeModal({ open, onOpenChange, feature, headline }) {
  const { trialEligible, trialing } = useTier() || {};
  const cta = getPlanCta({ trialEligible, trialing });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const isMulti = feature === "multiple_properties";
  const isWitness = feature === "witness_link";
  const isMaint = feature === "maintenance_share";
  const title = headline || (trialEligible ? "Try TenantSeal Plus free for 7 days"
    : isMulti ? "Upgrade for unlimited properties"
    : isWitness ? "Invite a witness with TenantSeal Plus"
    : isMaint ? "Share maintenance issues with TenantSeal Plus"
    : "Protect your full deposit");
  const subtext = isMulti
    ? "Your free plan includes one active rental. TenantSeal Plus unlocks unlimited properties plus everything you need to prepare your deposit response."
    : "TenantSeal Plus unlocks everything you need to prepare your response:";
  const bullets = isMulti ? [{ icon: Building2, text: "Unlimited properties" }, ...VALUE_PROPS] : VALUE_PROPS;

  const startCheckout = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.post("/billing/create-checkout-session", { origin: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      setLoading(false);
      setError(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="upgrade-modal" data-feature={feature || ""}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy">{title}</DialogTitle>
          <DialogDescription className="text-sm text-[#475569]">{subtext}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2.5 text-sm text-[#334155]">
          {bullets.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <f.icon size={17} className="mt-0.5 text-brand shrink-0" /> {f.text}
            </li>
          ))}
        </ul>
        <div className="rounded-xl bg-[#F1F5F9] px-4 py-3 text-center" data-testid="upgrade-modal-price">
          {trialEligible ? (
            <>
              <p className="font-heading text-lg font-bold text-navy">{cta.price}</p>
              <p className="text-sm text-[#64748B]">{cta.priceSub}</p>
            </>
          ) : (
            <>
              <span className="font-heading text-2xl font-bold text-navy">$6</span>
              <span className="text-sm text-[#64748B]">/month — cancel anytime</span>
            </>
          )}
        </div>
        {error && (
          <p data-testid="upgrade-modal-error" className="text-sm text-[#B91C1C] text-center">
            Couldn't start checkout — try again.
          </p>
        )}
        <Button data-testid="upgrade-modal-upgrade-btn" onClick={startCheckout} disabled={loading}
          className="w-full h-12 rounded-xl bg-navy hover:bg-navy-hover text-white text-base font-semibold">
          {loading ? <Loader2 size={18} className="mr-2 animate-spin" /> : (trialEligible ? <Sparkles size={16} className="mr-2" /> : <Lock size={16} className="mr-2" />)}
          {cta.cta}
        </Button>
        <Button data-testid="upgrade-modal-dismiss-btn" variant="ghost" onClick={() => onOpenChange(false)} className="w-full rounded-xl">
          Not now
        </Button>
        <div className="flex items-center justify-center gap-1.5 text-xs text-[#94A3B8]">
          <ShieldCheck size={13} /> Secure billing by Stripe
        </div>
        <p className="text-center text-[11px] text-[#94A3B8]" data-testid="upgrade-modal-legal">
          TenantSeal is not a law firm and does not provide legal advice.
        </p>
      </DialogContent>
    </Dialog>
  );
}
